import PropTypes from 'prop-types'
import React, {forwardRef, useImperativeHandle, useRef, useMemo} from 'react'
import {useChakraContext} from '@chakra-ui/react3'
import useVideoAutoplay from '../../../hooks/useVideoAutoplay'
import {hasValidVideoSource} from '../../../utils/utils'

// Common video MIME types
const VIDEO_EXTENSIONS = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    m4v: 'video/mp4',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2'
}

// Video format compression efficiency priority (lower = better compression)
const COMPRESSION_PRIORITY = {
    'video/av1': 1, // AV1 - best compression
    'video/webm': 2, // WebM/VP9 - very good compression
    'video/hevc': 3, // H.265/HEVC - good compression
    'video/mp4': 4, // H.264/MP4 - standard compression
    'video/ogg': 5, // Ogg/Theora - older format
    'video/quicktime': 6, // QuickTime/MOV - less efficient
    'video/x-msvideo': 7, // AVI - older format
    'video/x-ms-wmv': 8, // WMV - older format
    'video/x-flv': 9, // FLV - older format
    'video/3gpp': 10, // 3GP - mobile legacy
    'video/3gpp2': 11 // 3G2 - mobile legacy
}

/**
 * Detect video type from URL extension
 * @param {string} src - Video source URL
 * @returns {string|null} MIME type or null if not detected
 */
const detectVideoType = (src) => {
    if (!src) return null
    const extension = src.split('.').pop()?.split('?')[0]?.toLowerCase()
    return VIDEO_EXTENSIONS[extension] || null
}

/**
 * Get compression priority for a video type
 * @param {string|null|undefined} type - Video MIME type
 * @returns {number} Priority value (lower = better compression, 999 = unknown)
 */
const getCompressionPriority = (type) => {
    if (!type) return 999 // Unknown types go last
    return COMPRESSION_PRIORITY[type] || 999
}

/**
 * DirectVideo component for rendering native HTML5 video
 * Parent component controls play/pause via ref
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.sources - Array of video sources {src, type?, breakpoint?}
 * @param {Object} [props.poster] - Poster image object {src, alt?, title?}
 * @param {boolean} [props.autoplay=false] - Whether to autoplay the video (will be muted)
 * @param {boolean} [props.videoControls=false] - Whether to show native video controls
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy: 'eager' sets preload='auto', 'lazy' sets preload='metadata'
 * @param {string} [props.title] - Video title
 */
const DirectVideo = forwardRef(
    ({sources, poster, autoplay = false, videoControls = false, loading = 'lazy', title}, ref) => {
        const videoRef = useRef(null)
        const sys = useChakraContext()
        const shouldAutoplay = useVideoAutoplay(autoplay)

        // Process sources with breakpoint-based media queries and type detection
        const processedSources = useMemo(() => {
            if (!sources || !Array.isArray(sources) || sources.length === 0) {
                return []
            }

            const breakpointValues = sys.breakpoints.values || []
            const breakpointMap = Object.fromEntries(
                breakpointValues.map((bp) => [bp.name, bp.min])
            )

            return sources
                .filter(hasValidVideoSource)
                .map((source) => {
                    const processed = {...source}

                    // Auto-detect type if not provided
                    if (!processed.type) {
                        processed.type = detectVideoType(processed.src)
                    }

                    // Convert breakpoint to media query if breakpoint is specified
                    // Remove breakpoint for base/all (applies to all sizes)
                    if (processed.breakpoint === 'base' || processed.breakpoint === 'all') {
                        delete processed.breakpoint
                    } else if (processed.breakpoint) {
                        const minWidth = breakpointMap[processed.breakpoint]
                        if (minWidth) {
                            processed.media = `(min-width: ${minWidth})`
                        }
                    }

                    return processed
                })
                .sort((a, b) => {
                    // Primary sort: by breakpoint size (smaller first) for proper source ordering
                    // Sources without breakpoint (including base/all) come first
                    const aHasBreakpoint = !!a.breakpoint
                    const bHasBreakpoint = !!b.breakpoint

                    if (!aHasBreakpoint && !bHasBreakpoint) {
                        // Both have no breakpoint, sort by compression priority
                        return getCompressionPriority(a.type) - getCompressionPriority(b.type)
                    }
                    if (!aHasBreakpoint) return -1
                    if (!bHasBreakpoint) return 1

                    // Both have breakpoints
                    const aMin = breakpointMap[a.breakpoint] || '0px'
                    const bMin = breakpointMap[b.breakpoint] || '0px'
                    const breakpointDiff = parseInt(aMin) - parseInt(bMin)

                    // If same breakpoint, sort by compression priority
                    if (breakpointDiff === 0) {
                        return getCompressionPriority(a.type) - getCompressionPriority(b.type)
                    }

                    return breakpointDiff
                })
        }, [sources, sys.breakpoints.values])

        // Expose play/pause methods to parent
        useImperativeHandle(ref, () => ({
            play: () => {
                videoRef.current?.play()
            },
            pause: () => {
                videoRef.current?.pause()
            }
        }))

        if (processedSources.length === 0) {
            console.warn('DirectVideo: sources is required and must be a non-empty array')
            return null
        }

        const videoProps = {
            ref: videoRef,
            style: {width: '100%', height: '100%', objectFit: 'cover'},
            poster: poster?.src,
            title,
            playsInline: true
        }

        if (shouldAutoplay) {
            videoProps.autoPlay = true
            videoProps.muted = true
            videoProps.loop = true
        }

        if (videoControls) {
            videoProps.controls = true
        }

        if (loading === 'eager') {
            videoProps.preload = 'auto'
        } else {
            videoProps.preload = 'metadata'
        }

        return (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <video data-testid="direct-video" {...videoProps}>
                {processedSources?.map((source, index) => {
                    const {breakpoint, ...sourceProps} = source
                    return (
                        <source
                            key={index}
                            data-testid={`video-source-${index}`}
                            {...sourceProps}
                        />
                    )
                })}
            </video>
        )
    }
)

if (process.env.NODE_ENV === 'development') {
    DirectVideo.displayName = 'DirectVideo'
}

DirectVideo.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            type: PropTypes.string,
            breakpoint: PropTypes.string // Any breakpoint key from theme (xxs, xs, s, m, l, base, all, etc.)
        })
    ),
    poster: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        title: PropTypes.string
    }),
    autoplay: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    title: PropTypes.string
}

export default DirectVideo
